/**
 * Job Role Library (PR 1) UI types. They mirror docs/job-role-library-contract.md and the REAL wire shapes pinned by the checked-in
 * fixtures in __fixtures__ (see JobRoleWireContractTests on the API side). Two differences from the usual EPN convention: the Roles API
 * sends its status enums as NAMES ("Draft", "Active", "Archived"), which wireMapping.ts turns into the numbers below (and back for the
 * list filter), and the employment guidance values (typical pay type and so on) are free text, not numeric enums.
 *
 * Terminology: a "role" in this feature is a JOB role (what someone does at the company). It is never a system access role
 * (what someone can do in EPN) and it never carries a permission.
 */

import type { BenchmarkStatus, EmployerPayBandSummary } from './compensationTypes';
import { emptyClassification, type JobRoleClassification } from './guidedTypes';

// PR 2 (pay bands and benchmarks) types live in compensationTypes.ts and are re-exported so callers keep one import path.
export * from './compensationTypes';
// Guided fields (reference options, occupation lookup, capabilities, classification) live in guidedTypes.ts, re-exported the same way.
export * from './guidedTypes';

/** `JobTitle.Status` (Draft=1, Active=2, Archived=3), exactly as in the contract. */
export const JobRoleStatus = { Draft: 1, Active: 2, Archived: 3 } as const;
export const jobRoleStatusLabels: Record<number, string> = { 1: 'Draft', 2: 'Active', 3: 'Archived' };

/** `JobRoleVersion.Status` (Draft, Published, Superseded). */
export const JobRoleVersionStatus = { Draft: 1, Published: 2, Superseded: 3 } as const;
export const jobRoleVersionStatusLabels: Record<number, string> = { 1: 'Draft', 2: 'Published', 3: 'Superseded' };

/** Suggested values for the free-text employment guidance fields. The server stores whatever the employer types (so a stored value
 * outside these lists must still be shown, and never dropped on a round trip). */
export const TYPICAL_EMPLOYMENT_TYPE_PRESETS = ['Employee', 'Full-time', 'Part-time', 'Seasonal', 'Temporary'] as const;
export const TYPICAL_PAY_TYPE_PRESETS = ['Hourly', 'Salary', 'Salary and commission', 'Commission'] as const;
export const HOURLY_OR_SALARIED_PRESETS = ['Hourly', 'Salaried'] as const;

export interface JobRoleQualifications {
  minimumEducation: string | null;
  preferredEducation: string | null;
  minimumExperience: string | null;
  preferredExperience: string | null;
  licenses: string[];
  certifications: string[];
  technicalSkills: string[];
  professionalSkills: string[];
  preferredSkills: string[];
  languages: string[];
  travel: string | null;
  workArrangement: string | null;
  physicalRequirements: string | null;
  workingConditions: string | null;
}

/** Employer guidance only. `flsaClassification`, `overtimeEligible` and the other eligibility flags are the EMPLOYER's own
 * classification, never an automatic legal conclusion, and the UI always shows a review warning beside them. */
export interface JobRoleEmploymentGuidance {
  /** Free text (for example "Employee"); null = not stated. */
  typicalEmploymentType: string | null;
  /** Free text (for example "Hourly", "Salary", "Salary and commission"); null = not stated. Never a rate. */
  typicalPayType: string | null;
  hourlyOrSalaried?: string | null;
  fullOrPartTime?: string | null;
  flsaClassification: string | null;
  commissionEligible: boolean | null;
  bonusEligible: boolean | null;
  overtimeEligible: boolean | null;
  benefitEligibilityGuidance: string | null;
}

/** The validated, size-limited `ContentJson` document of a version or template. */
export interface JobRoleContent {
  purpose: string | null;
  summary: string | null;
  alternateTitles: string[];
  essentialDuties: string[];
  responsibilities: string[];
  expectedOutcomes: string[];
  decisionAuthority: string | null;
  supervisoryResponsibilities: string | null;
  internalRelationships: string | null;
  externalRelationships: string | null;
  reportsTo: string | null;
  /** Guided fields additions (all optional on the wire; an older draft has none of them). */
  budgetAuthority: string | null;
  complianceResponsibilities: string[];
  performanceIndicators: string[];
  classification: JobRoleClassification;
  qualifications: JobRoleQualifications;
  employmentGuidance: JobRoleEmploymentGuidance;
}

export function emptyJobRoleContent(): JobRoleContent {
  return {
    purpose: null,
    summary: null,
    alternateTitles: [],
    essentialDuties: [],
    responsibilities: [],
    expectedOutcomes: [],
    decisionAuthority: null,
    supervisoryResponsibilities: null,
    internalRelationships: null,
    externalRelationships: null,
    reportsTo: null,
    budgetAuthority: null,
    complianceResponsibilities: [],
    performanceIndicators: [],
    classification: emptyClassification(),
    qualifications: {
      minimumEducation: null, preferredEducation: null, minimumExperience: null, preferredExperience: null,
      licenses: [], certifications: [], technicalSkills: [], professionalSkills: [], preferredSkills: [], languages: [],
      travel: null, workArrangement: null, physicalRequirements: null, workingConditions: null,
    },
    employmentGuidance: {
      typicalEmploymentType: null, typicalPayType: null, flsaClassification: null, commissionEligible: null,
      bonusEligible: null, overtimeEligible: null, benefitEligibilityGuidance: null,
    },
  };
}

/** A server document may omit sections or send nulls for lists (a legacy role has no document at all). This fills every gap so
 * screens never branch on a missing section. */
export function normalizeJobRoleContent(raw: Partial<JobRoleContent> | null | undefined): JobRoleContent {
  const base = emptyJobRoleContent();
  if (!raw) return base;
  const list = (value: string[] | null | undefined) => (Array.isArray(value) ? value : []);
  return {
    ...base,
    ...raw,
    alternateTitles: list(raw.alternateTitles),
    essentialDuties: list(raw.essentialDuties),
    responsibilities: list(raw.responsibilities),
    expectedOutcomes: list(raw.expectedOutcomes),
    complianceResponsibilities: list(raw.complianceResponsibilities),
    performanceIndicators: list(raw.performanceIndicators),
    classification: { ...base.classification, ...raw.classification },
    qualifications: {
      ...base.qualifications,
      ...raw.qualifications,
      licenses: list(raw.qualifications?.licenses),
      certifications: list(raw.qualifications?.certifications),
      technicalSkills: list(raw.qualifications?.technicalSkills),
      professionalSkills: list(raw.qualifications?.professionalSkills),
      preferredSkills: list(raw.qualifications?.preferredSkills),
      languages: list(raw.qualifications?.languages),
    },
    employmentGuidance: { ...base.employmentGuidance, ...raw.employmentGuidance },
  };
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ---- templates (global, read-only, maintained by EPN) ----

export interface JobRoleTemplateListItem {
  id: string;
  code: string;
  title: string;
  socCode: string | null;
  onetCode: string | null;
  jobFamily: string | null;
  careerLevel: string | null;
  suggestedDepartmentName: string | null;
  templateVersion: number;
}

export interface JobRoleTemplateDetail extends JobRoleTemplateListItem {
  content: JobRoleContent;
}

// ---- company roles ----

/** One row of `GET /api/job-roles`. `hasDraft` is NOT in the contract's list item: it is optional here so the Roles page can
 * offer "Edit" rather than "Create New Version" for an Active role that already has an open draft (BACKEND GAP, non-blocking). */
export interface JobRoleListItem {
  id: string;
  roleCode: string | null;
  title: string;
  departmentId: string | null;
  departmentName: string | null;
  jobFamily: string | null;
  careerLevel: string | null;
  status: number;
  effectiveFromUtc: string | null;
  currentVersionNumber: number | null;
  employeesAssigned: number;
  typicalPayType: string | null;
  concurrencyVersion: number;
  /** Not on the real list item (a known, harmless limitation); the mock sends it so the screen can be exercised both ways. */
  hasDraft?: boolean | undefined;
  /** The role's CURRENT employer pay band (needs `roles.read`), or null when none is set. Company pay policy, never an employee's pay. */
  employerPayBand: EmployerPayBandSummary | null;
  /** Whether market benchmarks exist for the role. `NotPermitted` for a caller without `rolecompensationbenchmarks.read`. */
  benchmarkStatus: BenchmarkStatus;
}

/** The server's sort keys; a leading dash means descending. (There is no sort by employee count.) */
export type JobRoleSort =
  | 'title' | '-title' | 'code' | '-code' | 'status' | '-status' | 'department' | '-department' | 'family' | '-family'
  | 'level' | '-level' | 'paytype' | '-paytype' | 'effective' | '-effective';

export interface ListJobRolesParams {
  search?: string | undefined;
  departmentId?: string | undefined;
  jobFamily?: string | undefined;
  careerLevel?: string | undefined;
  status?: number | undefined;
  /** Free text, matched case-insensitively and exactly (for example "Salary"). */
  payType?: string | undefined;
  sort?: JobRoleSort | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface JobRoleDetail {
  id: string;
  roleCode: string | null;
  title: string;
  /** The role's short description (a header field, replaced in full by every update). */
  description?: string | null;
  departmentId: string | null;
  departmentName: string | null;
  jobFamily: string | null;
  careerLevel: string | null;
  managementLevel: string | null;
  socCode: string | null;
  onetCode: string | null;
  status: number;
  effectiveFromUtc: string | null;
  currentVersionNumber: number | null;
  sourceTemplateId: string | null;
  concurrencyVersion: number;
  hasDraft: boolean;
  draftVersionNumber?: number | null;
  employeesAssigned?: number;
  typicalPayType?: string | null;
  createdAtUtc?: string | null;
  updatedAtUtc?: string | null;
  /** The number and state of the version `content` belongs to (the Published one in force, or the Draft when never activated). */
  versionNumber?: number | null;
  versionStatus?: number | null;
  content: JobRoleContent;
  /** The pending Draft (null when there is none): this is what an editor loads. */
  draftContent: JobRoleContent | null;
  employerPayBand: EmployerPayBandSummary | null;
  benchmarkStatus: BenchmarkStatus;
}

export interface JobRoleHeaderInput {
  title: string;
  description?: string | null | undefined;
  roleCode?: string | null | undefined;
  departmentId?: string | null | undefined;
  jobFamily?: string | null | undefined;
  careerLevel?: string | null | undefined;
  managementLevel?: string | null | undefined;
  socCode?: string | null | undefined;
  onetCode?: string | null | undefined;
}

export interface CreateJobRoleInput extends JobRoleHeaderInput {
  content?: JobRoleContent | undefined;
}

export interface CreateFromTemplateInput {
  title?: string | undefined;
  roleCode?: string | null | undefined;
  departmentId?: string | null | undefined;
}

export interface DuplicateJobRoleInput {
  title: string;
  roleCode?: string | null | undefined;
}

export interface UpdateJobRoleInput extends JobRoleHeaderInput {
  content: JobRoleContent;
  expectedConcurrencyVersion: number;
}

export interface JobRoleVersionItem {
  id: string;
  versionNumber: number;
  status: number;
  effectiveFromUtc: string | null;
  effectiveToUtc: string | null;
  /** A user id, not a name: the screen never shows it as if it were one. */
  createdByUserId: string | null;
  createdAtUtc: string | null;
  publishedAtUtc: string | null;
}

export interface JobRoleVersionDetail extends JobRoleVersionItem {
  contentSchemaVersion?: number;
  content: JobRoleContent;
}

/** Assigned Employees tab: names and numbers only. The contract forbids pay or any other sensitive field here. */
export interface JobRoleEmployee {
  employeeId: string;
  displayName: string;
  employeeNumber: string;
  /** `yyyy-MM-dd`; null when the server has no start date for the assignment. */
  effectiveFrom: string | null;
}

/** One audit event of a role (`GET /api/job-roles/{id}/audit`). `metadataJson` holds ids and changed field NAMES only. */
export interface JobRoleAuditItem {
  id: string;
  occurredAtUtc: string;
  actorUserId: string | null;
  actorEmailSnapshot: string | null;
  eventType: string;
  metadataJson: string | null;
}

export interface PageParams {
  page?: number | undefined;
  pageSize?: number | undefined;
}

// ---- employee role assignments ----

export interface CreateEmployeeJobRoleAssignmentInput {
  jobTitleId: string;
  effectiveFromDate?: string | undefined;
  reason?: string | undefined;
  idempotencyKey?: string | undefined;
}

export interface EmployeeJobRoleAssignment {
  id: string;
  jobTitleId: string;
  roleTitle: string;
  roleCode: string | null;
  /** The role's status NOW ("Active", "Archived"...), as the server sends it. */
  roleStatus: string;
  jobRoleVersionId: string | null;
  versionNumber: number | null;
  isPrimary: boolean;
  /** `yyyy-MM-dd` */
  effectiveFrom: string;
  effectiveTo: string | null;
  /** A user id, not a name: never shown as if it were one. */
  assignedByUserId: string | null;
  reason: string | null;
}

/** Stable problem codes the Roles API sends in `code` (contract section 4). Never shown to users. */
export const JobRoleErrorCode = {
  ConcurrencyConflict: 'CONCURRENCY_CONFLICT',
  NotDraft: 'ROLE_NOT_DRAFT',
  NotAssignable: 'ROLE_NOT_ASSIGNABLE',
  /** PR 2: a pay band cannot be set without the review acknowledgement (400). */
  PayBandReviewRequired: 'PAY_BAND_REVIEW_REQUIRED',
  /** PR 2: a new pay band cannot be set on an Archived role (409). */
  RoleNotEditable: 'ROLE_NOT_EDITABLE',
} as const;

/** Suggestion lists for the free-text Job Family / Level fields (the server stores whatever the employer types). */
export const JOB_FAMILY_SUGGESTIONS = [
  'Finance and Accounting', 'Human Resources', 'Operations', 'Sales', 'Customer Support', 'Engineering', 'Executive Leadership',
] as const;
export const CAREER_LEVEL_SUGGESTIONS = ['Entry', 'Intermediate', 'Senior', 'Manager', 'Director', 'Executive'] as const;

export const EMPLOYER_CLASSIFICATION_WARNING =
  'Employer classification, not a legal determination. EPN does not decide whether a position is exempt or non-exempt, or eligible for overtime. '
  + 'Review this classification with your payroll or legal advisor before relying on it.';

export const ROLE_VS_ACCESS_EXPLAINER =
  'A job role describes what someone does at your company. A system access role controls what someone can do in EPN. Assigning a job role never changes anyone’s access.';
